import Chip from '@mui/material/Chip'
import { Button } from '@mui/material'
import { useState } from 'react'
import PageTransition from 'components/PageTransition'
import { Outlet, useMatch, useNavigate, useParams } from 'react-router-dom'
import ErrorFallback from 'components/ErrorFallback'
import { CircularProgress } from '@mui/material'
import { usePharmacyTreatments } from 'data/treatments'
import EmptyState from 'components/EmptyState'
import { formatDate, formatTime } from 'utils'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { ITEMS_PER_PAGE } from 'app-constants'
import { DateRangeProps } from 'react-date-range'
import { Staff } from 'types/hospital'
import { addDays, format } from 'date-fns'
import { Treatment } from 'types/treatments'
import { usePharmacy } from 'data/pharmacy'
import Filter from './Filter'

const chipColor = {
  completed: 'success',
  ongoing: 'info',
  cancelled: 'error',
  pending: 'primary',
} as const

export default function PharmacyTreatments() {
  const isPage = !!useMatch('/providers/pharmacies/:id/medications/treatments')
  const isRefillListPage = !!useMatch(
    '/providers/clinics/:id/treatments/:id/medication/:id/refill-history'
  )

  const navigate = useNavigate()
  // const { data: hospitalStaff } = useHospitalStaff(hospitalId as string)
  const { id } = useParams()
  const { data: pharmacy } = usePharmacy(id as string)
  const [selectionRange, setSelectionRange] = useState<DateRangeProps['ranges'] | undefined>()
  const [diagnosis, setDiagnosis] = useState<string | undefined>('')
  const [status, setStatus] = useState<Treatment['treatment_status'] | undefined>()

  const [currentPage, setCurrentPage] = useState(0)
  const sameRangeDate = selectionRange?.[0].startDate === selectionRange?.[0].endDate
  const formattedStartDate = selectionRange
    ? format(selectionRange?.[0].startDate as Date, 'yyyy-MM-dd')
    : undefined
  const formattedEndDate = selectionRange
    ? format(addDays(selectionRange?.[0].endDate as Date, 1), 'yyyy-MM-dd')
    : undefined

  const {
    data: treatments,
    isLoading,
    isError,
    refetch,
  } = usePharmacyTreatments({
    offset: ITEMS_PER_PAGE * currentPage,
    limit: ITEMS_PER_PAGE,
    pharmacy_id: pharmacy?.id as string,
    from_date: sameRangeDate ? undefined : formattedStartDate,
    to_date: sameRangeDate ? undefined : formattedEndDate,
    date: sameRangeDate ? formattedStartDate : undefined,
    diagnosis: diagnosis || undefined,
    treatment_status: status || undefined,
    provider: 'pharmacy',
  })

  return (
    <>
      {/* <p className="text-center text-sm font-medium mb-4 lg:text-left my-4">
        {isPage ? 'Treatments' : isRefillListPage ? 'Refill History' : 'Treatments Details'}
      </p> */}
      <PageTransition show={!isPage}>
        <Outlet />
      </PageTransition>
      <PageTransition show={isPage}>
        <div className="grid lg:w-[39rem] mx-auto gap-4">
          <div className="grid place-items-end">
            <Filter
              filterByDate={(data) => setSelectionRange(data as DateRangeProps['ranges'])}
              filterByDiagnosis={(data) => {
                setDiagnosis(data as string)
              }}
              filterByStatus={(data) => {
                setStatus(data as Treatment['treatment_status'])
              }}
              dateRange={selectionRange}
              diagnosis={diagnosis}
              status={status}
              key={`${status}-${diagnosis}`}
            />
          </div>
          {isLoading ? (
            <div className="grid place-items-center mt-40 overflow-hidden">
              <CircularProgress />
            </div>
          ) : isError ? (
            <div>
              <ErrorFallback
                message="Something went wrong"
                description="We could not fetch treatments"
                reset={refetch}
                buttonText="Try again"
              />
            </div>
          ) : treatments?.item_count === 0 ? (
            <EmptyState text="There are no treatment yet" />
          ) : (
            <div className="grid gap-4">
              {treatments?.items?.map((item) => {
                return (
                  <div
                    key={item.id}
                    className="rounded-xl p-5 border grid border-default font-medium gap-4"
                  >
                    <p>
                      <span className="font-semibold">Start Date</span>
                      {' : '}
                      {formatDate(item?.treatment_start_date)}{' '}
                    </p>

                    <p>
                      <span className="font-semibold">Time</span>
                      {' : '}
                      {formatTime(item?.created_at)}{' '}
                    </p>

                    <p>
                      <span className="font-semibold">Pharmacist </span> {' : '} {item?.metadata?.staff_name}
                    </p>

                    <p>
                      <span className="font-semibold">Diagnosis</span> {' : '} {item?.diagnosis}{' '}
                    </p>

                    <p>
                      {' '}
                      <span className="font-semibold">Treatment</span> {' : '}{' '}
                      {item?.treatment_name}{' '}
                    </p>

                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">Status </p>
                        {' : '}{' '}
                        <Chip
                          color={chipColor[item.treatment_status]}
                          label={item?.treatment_status}
                        />
                      </div>
                   
                    </div>

                       <button
                        className="rounded-xl text-brand-700 w-fit"
                        onClick={() => navigate(item.id)}
                      >
                        View Details
                      </button>
                  </div>
                )
              })}
              {treatments && (
                <div className="justify-between text-neutral-500 mt-20 flex">
                  <Button
                    startIcon={<ArrowBackIcon />}
                    variant="outlined"
                    onClick={() => {
                      setCurrentPage((prev) => prev - 1)
                    }}
                    disabled={currentPage + 1 === 1}
                    className="px-3 border border-default text-neutral-500"
                  >
                    Previous
                  </Button>
                  <div className="page-number">
                    Page {currentPage + 1} of {Math.ceil(treatments?.item_count / ITEMS_PER_PAGE)}
                  </div>
                  <Button
                    endIcon={<ArrowForwardIcon className="text-error-900" />}
                    variant="outlined"
                    onClick={() => {
                      setCurrentPage((prev) => prev + 1)
                    }}
                    disabled={
                      currentPage + 1 === Math.ceil(treatments?.item_count / ITEMS_PER_PAGE)
                    }
                    className="px-3 py-1 border border-error-600 text-error-900"
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </PageTransition>
    </>
  )
}
